import { z } from "zod";
import { ZDateish, ZObjectIdString, ZProjectNo } from "./foundationSchema";
import { ZDrofusInterfaceDiscipline } from "./drofusInterfaceOccurrence.schema";
const nullableString = z.string().nullable().optional();
const nullableNumber = z.number().nullable().optional();
const nullableBoolean = z.boolean().nullable().optional();
const ZJsonValue = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(ZJsonValue),
    z.record(z.string(), ZJsonValue),
]));
export const ZDrofusInterfaceChangeType = z.enum([
    "added",
    "removed",
    "updated",
]);
export const ZDrofusInterfaceChangeSeverity = z.enum([
    "low",
    "medium",
    "high",
]);
export const ZDrofusInterfaceChangeEventStatus = z.enum([
    "open",
    "acknowledged",
    "closed",
]);
export const ZDrofusInterfaceChangeEventEntityType = z.enum([
    "occurrence",
    "room",
]);
export const ZDrofusInterfaceChangeEventFieldGroup = z.enum([
    "occurrence",
    "room",
    "article",
    "system",
    "tfmStatus",
    "occurrenceData",
    "articleNeeds",
    "roomData",
    "metadata",
]);
export const ZDrofusInterfaceChangeEventContext = z
    .object({
    occurrence: z
        .object({
        mmiCode: nullableString,
        mmiIds: z.array(z.number()).nullable().optional(),
        programmedStatusIds: z.array(z.number()).nullable().optional(),
    })
        .optional(),
    room: z
        .object({
        id: nullableNumber,
        functionNo: nullableString,
        architectNo: nullableString,
        fullName: nullableString,
    })
        .optional(),
    article: z
        .object({
        id: nullableNumber,
        compCode: nullableString,
        name: nullableString,
        responsibility: nullableString,
        toBeDrawn: nullableBoolean,
    })
        .optional(),
})
    .default({});
export const ZDrofusInterfaceChangeEventLogAttribution = z.object({
    username: nullableString,
    time: ZDateish.optional().nullable(),
    field: nullableString,
    action: nullableString,
    oldValue: z.unknown().optional(),
    newValue: z.unknown().optional(),
    note: nullableString,
    changeSource: nullableString,
    changeList: nullableString,
    confidence: z.enum(["exact", "probable", "occurrence"]).default("occurrence"),
});
export const ZDrofusInterfaceChangeEvent = z.object({
    _id: ZObjectIdString.optional(),
    projectNo: ZProjectNo,
    source: z.literal("drofus").default("drofus"),
    entityType: ZDrofusInterfaceChangeEventEntityType,
    runId: ZObjectIdString,
    baseRunId: ZObjectIdString,
    runStartedAt: ZDateish,
    runFinishedAt: ZDateish.optional().nullable(),
    baseRunStartedAt: ZDateish.optional().nullable(),
    entityId: z.union([z.string().min(1), z.number().int()]),
    stableKey: z.string().min(1),
    changeType: ZDrofusInterfaceChangeType,
    changeKey: z.string().min(1),
    fieldId: z.string().min(1).optional(),
    rawFieldId: z.string().min(1).optional(),
    displayName: z.string().min(1).optional(),
    group: ZDrofusInterfaceChangeEventFieldGroup.optional(),
    normalizedPath: z.string().min(1).optional(),
    oldValue: ZJsonValue.optional(),
    newValue: ZJsonValue.optional(),
    oldContentHash: z.string().min(1).optional(),
    newContentHash: z.string().min(1).optional(),
    oldNormalizedHash: z.string().min(1).optional(),
    newNormalizedHash: z.string().min(1).optional(),
    context: ZDrofusInterfaceChangeEventContext,
    drofusLog: ZDrofusInterfaceChangeEventLogAttribution.optional(),
    affectedDisciplines: z.array(ZDrofusInterfaceDiscipline).default([]),
    severity: ZDrofusInterfaceChangeSeverity,
    ruleId: z.string().min(1).optional(),
    status: ZDrofusInterfaceChangeEventStatus.default("open"),
    createdAt: ZDateish,
    acknowledgedAt: ZDateish.optional().nullable(),
    acknowledgedBy: z.string().optional().nullable(),
    closedAt: ZDateish.optional().nullable(),
    closedBy: z.string().optional().nullable(),
    meta: z
        .object({
        schemaVersion: z.string().default("1"),
        selectHash: z.string().min(1).optional(),
        diffAlgorithmVersion: z.string().min(1).optional(),
    })
        .optional(),
});
